import { Component, DestroyRef, Input, OnInit } from '@angular/core';
import { faFilter } from '@fortawesome/free-solid-svg-icons';
import { SearchForm } from '../../../../core/models/forms/SearchForm';
import { DisplayableFilters, DocumentsViewSettings } from '../view-settings/viewSettings';
import { BehaviorSubject } from 'rxjs';
import { FreightDocumentStatus } from '../../../../core/models/freight-document/FreightDocumentStatus';
import { FiltersStorageService, FilterType } from '../../../../core/services/filters-storage.service';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { NgbDateParserFormatter } from '@ng-bootstrap/ng-bootstrap';
import { SearchUtils } from '../../../../core/utils/SearchUtils';
import { tap } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommentEventType } from '../../../../core/models/freight-document/CommentEventType';
import { CommentImportance } from '../../../../core/models/freight-document/CommentImportance';
import { MultiSelectData, MultiselectOption } from '../../../shared-portal/multiselect/multiselect.component';
import { FormControl } from '@angular/forms';
import { FreightDocumentFlagService } from '../../../../core/services/freight-document-flag.service';

@Component({
  selector: 'app-documents-search-bar',
  templateUrl: './documents-search-bar.component.html',
  styleUrls: ['./documents-search-bar.component.scss']
})
export class DocumentsSearchBarComponent implements OnInit {
  filterIcon = faFilter;
  private FURTHER_INSPECTION = 'inspection';

  @Input() loading = false;
  @Input() viewSettings: DocumentsViewSettings;

  filtersExpanded = false;
  searchForm = new SearchForm();
  periodControl = new FormControl<number>(null);

  private statusDropdownData = [FreightDocumentStatus.ISSUED, FreightDocumentStatus.TRANSIT, FreightDocumentStatus.DELIVERED, this.FURTHER_INSPECTION, FreightDocumentStatus.DRAFT, FreightDocumentStatus.CANCELLED];
  statusMultiselectData = new MultiSelectData();
  withFlagsMultiselectData = new MultiSelectData();
  withoutFlagsMultiselectData = new MultiSelectData();
  observationImportance$: BehaviorSubject<CommentImportance[]> = new BehaviorSubject([]);
  observationEvents$: BehaviorSubject<CommentEventType[]> = new BehaviorSubject([]);

  constructor(private filtersStorage: FiltersStorageService,
              private router: Router,
              private route: ActivatedRoute,
              private dateFormatter: NgbDateParserFormatter,
              private destroyRef: DestroyRef,
              private flagService: FreightDocumentFlagService) {
  }

  ngOnInit(): void {
    this.initSearchForm();
    this.initStoredFilters();
    this.initViewSettings();
    this.applyFilters();
    this.registerOnStatusListChanges();
    this.initFlagFilters();
  }

  private initSearchForm(storedFilters?: Params): void {
    const queryParams = storedFilters ? storedFilters : this.route.snapshot.queryParams;
    this.searchForm.patchValue(SearchUtils.getSearchFormData(queryParams, this.dateFormatter));
    this.observationImportance$.next(SearchUtils.getObservationImportance(queryParams));
    this.observationEvents$.next(SearchUtils.getObservationEventType(queryParams));

    SearchUtils.initFreightDocumentStatuses(this.searchForm, queryParams);
    if (!storedFilters) {
      this.statusMultiselectData.options.push(...this.getStatusListOptions(this.statusDropdownData));
    }
    this.statusMultiselectData.control.setValue(this.getStatusListOptions(Object.keys(this.searchForm.statuses.value)));
    this.syncFlagSelections();
  }

  private initStoredFilters(): void {
    const storedFilters = this.filtersStorage.get(FilterType.FREIGHT_DOCUMENTS);
    // Use stored filters only when there is no filters provided in URL beforehand
    if (storedFilters && this.searchForm.getTotalNumberOfFilters() === 0) {
      this.initSearchForm(storedFilters);
    }
  }

  private initViewSettings(): void {
    this.periodControl.setValue(this.viewSettings.getPeriod());
  }

  applyFilters(): void {
    const appliedFilters: Params = SearchUtils.getQueryParams(this.searchForm.value, this.dateFormatter);
    this.router
      .navigate(['.'], {
        relativeTo: this.route,
        queryParams: appliedFilters,
        queryParamsHandling: 'merge'
      })
      .then(() => {
        if (this.searchForm.getTotalNumberOfFilters() > 0) {
          this.filtersStorage.set(appliedFilters, FilterType.FREIGHT_DOCUMENTS);
        } else {
          this.filtersStorage.clear(FilterType.FREIGHT_DOCUMENTS);
        }
      });
  }

  clearFilters(): void {
    this.searchForm.reset();
    this.statusMultiselectData.control.reset();
    this.withFlagsMultiselectData.control.reset();
    this.withoutFlagsMultiselectData.control.reset();
    this.filtersStorage.clear(FilterType.FREIGHT_DOCUMENTS);
    this.applyFilters();
  }

  private registerOnStatusListChanges() {
    return this.statusMultiselectData.control.valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef),
      tap((newValue) => {
        Object.keys(FreightDocumentStatus).forEach(key => this.searchForm.statuses.removeControl(key));
        if (newValue) {
          const statuses = newValue.map((val) => this.statusDropdownData[val.id])
            .filter(val => val !== this.FURTHER_INSPECTION);
          this.searchForm.appendStatuses(statuses);
          this.searchForm.furtherInspection.setValue(
            newValue.some((val) => this.statusDropdownData[val.id] === this.FURTHER_INSPECTION)
          );
        }
      })
    ).subscribe();
  }

  private initFlagFilters(): void {
    this.flagService.getAll().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (flags) => {
        const options = flags.map((flag) => ({ id: flag.flagId, label: flag.name }) as MultiselectOption);
        this.withFlagsMultiselectData.options.push(...options);
        this.withoutFlagsMultiselectData.options.push(...options);
        this.dropUnknownFlagFilters();
      }
    });
    this.registerOnFlagListChanges(this.withFlagsMultiselectData, this.searchForm.withFlags);
    this.registerOnFlagListChanges(this.withoutFlagsMultiselectData, this.searchForm.withoutFlags);
  }

  private syncFlagSelections(): void {
    this.selectFlagOptions(this.withFlagsMultiselectData, this.searchForm.withFlags);
    this.selectFlagOptions(this.withoutFlagsMultiselectData, this.searchForm.withoutFlags);
  }

  private dropUnknownFlagFilters(): void {
    const before = JSON.stringify([this.searchForm.withFlags.value, this.searchForm.withoutFlags.value]);
    this.syncFlagSelections();
    this.keepSelectedFlags(this.withFlagsMultiselectData, this.searchForm.withFlags);
    this.keepSelectedFlags(this.withoutFlagsMultiselectData, this.searchForm.withoutFlags);
    if (JSON.stringify([this.searchForm.withFlags.value, this.searchForm.withoutFlags.value]) !== before) {
      this.applyFilters();
    }
  }

  private keepSelectedFlags(data: MultiSelectData, control: FormControl<number[]>): void {
    const selected = data.control.value;
    control.setValue(selected?.length ? selected.map((option) => option.id) : null);
  }

  private selectFlagOptions(data: MultiSelectData, control: FormControl<number[]>): void {
    data.control.setValue(data.options.filter((option) => control.value?.includes(option.id)), { emitEvent: false });
  }

  private registerOnFlagListChanges(data: MultiSelectData, control: FormControl<number[]>): void {
    data.control.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((selected) => control.setValue(selected?.length ? selected.map((option) => option.id) : null));
  }

  clearSearchText(): void {
    this.searchForm.searchText.reset();
    this.applyFilters();
  }

  toggleAdvancedFilters(): void {
    this.filtersExpanded = !this.filtersExpanded;
  }

  filtersChanged(filters: DisplayableFilters) {
    this.viewSettings.updateFilters(filters);
  }

  updatePeriod(): void {
    this.viewSettings.updatePeriod(this.periodControl.value);
  }

  private getStatusListOptions(statuses: string[]): MultiselectOption[] {
    return statuses.map(status => ({
      id: this.statusDropdownData.indexOf(status),
      label: 'freightDocument.status.' + status
    }) as MultiselectOption);
  }
}
